import React, { PureComponent, FunctionComponent, ReactNode } from 'react';
import { DEVICE_MANAGER_SETTINGS } from 'app/models/localStorage/constants';
import { DEVICE_MANAGER_STATE } from 'app/models/sessionStorage/constants';
import createStorageConsumer from 'app/components/utilities/createStorageConsumer';
import { AUTOMATIC_DEVICE_ID, DeviceState } from 'app/services/rtc/DeviceManager/DeviceState';
import defaultsDeep from 'lodash/defaultsDeep';
import isEqual from 'lodash/isEqual';
import TrackState from 'app/services/rtc/TrackState';
import { createSelector } from 'reselect';

/**
 * Subset necessary for calculate what's persisted
 */
type DeviceStateRequiredSubset = Pick<DeviceState, 'trackState' | 'isSkipped' | 'preferredDeviceId' | 'autoGain'>;

/**
 * Subset of DeviceState to calculate persisted value from
 */
interface IDeviceManagerStateSubset {
  audio: DeviceStateRequiredSubset;
  video: DeviceStateRequiredSubset;
}

export interface IDeviceState {
  /**
   * Flag telling if the device was unmuted last time during within
   * this session
   */
  active: boolean;
}

export interface IDeviceSettings {
  /**
   * Id of the last used device
   */
  preferredDeviceId: string;
}

export interface IAudioInputSettings extends IDeviceSettings {
  autoGain: boolean;
}

/**
 * Device state that's stored in session storage
 */
export interface IDevicesState { audio: IDeviceState; video: IDeviceState; }

/**
 * Device settings stored in local storage
 */
export interface IDevicesSettings {
  audio: IAudioInputSettings;
  video: IDeviceSettings;
}

/**
 * Combined devices' state and settings from session and local
 * storage
 */
export interface IDevicesPersistedState {
  audio: IDeviceState & IAudioInputSettings;
  video: IDeviceState & IDeviceSettings;
}

const defaultSettings: IDevicesSettings = {
  audio: {
    preferredDeviceId: AUTOMATIC_DEVICE_ID,
    autoGain: true,
  },
  video: {
    preferredDeviceId: AUTOMATIC_DEVICE_ID,
  },
};

const defaultState: IDevicesState = {
  audio: {
    active: true,
  },
  video: {
    active: true,
  },
};

const SettingsStorage = createStorageConsumer<IDevicesSettings>(
  'localStorage',
  DEVICE_MANAGER_SETTINGS,
  {
    serialize: (settings) => settings,
    deserialize: (value: unknown): IDevicesSettings => defaultsDeep({}, value, defaultSettings),
  },
);

const StateStorage = createStorageConsumer<IDevicesState>(
  'sessionStorage',
  DEVICE_MANAGER_STATE,
  {
    serialize: (state) => state,
    deserialize: (value: unknown): IDevicesState => defaultsDeep({}, value, defaultState),
  },
);

interface IProps {
  children: (args: {
    value: IDevicesPersistedState,
    update: (managerState: IDeviceManagerStateSubset) => void,
  }) => ReactNode;
}

interface IDeps {
  settings: IDevicesSettings;
  state: IDevicesState;
  saveSettings: (value: IDevicesSettings) => void;
  saveState: (value: IDevicesState) => void;
}

class DeviceManagerPersistence extends PureComponent<IProps & IDeps> {
  private readonly mergeSettingsAndState = createSelector(
    ({ settings }: IDeps) => settings,
    ({ state }: IDeps) => state,
    (settings: IDevicesSettings, state: IDevicesState): IDevicesSettings & IDevicesState =>
      defaultsDeep({}, settings, state),
  );

  public render() {
    return this.props.children({
      value: this.mergeSettingsAndState(this.props),
      update: this.update,
    });
  }

  private readonly update = (managerState: IDeviceManagerStateSubset) => {
    const { settings, saveSettings, state, saveState } = this.props;

    const newSettings = {
      audio: dumpAudioInputSettings(managerState.audio),
      video: dumpDeviceSettings(managerState.video),
    };

    const newState = {
      audio: dumpDeviceState(managerState.audio),
      video: dumpDeviceState(managerState.video),
    };

    if (!isEqual(newSettings, settings)) {
      saveSettings(newSettings);
    }

    if (!isEqual(newState, state)) {
      saveState(newState);
    }
  };
}

const dumpDeviceSettings = (device: DeviceStateRequiredSubset): IDeviceSettings => ({
  preferredDeviceId: device.preferredDeviceId || AUTOMATIC_DEVICE_ID,
});

const dumpAudioInputSettings = (device: DeviceStateRequiredSubset): IAudioInputSettings => ({
  ...dumpDeviceSettings(device),
  autoGain: device.autoGain,
});

const dumpDeviceState = (device: DeviceStateRequiredSubset): IDeviceState => ({
  //  Device is active if there were no explicit user actions to
  //  disable it, either by skipping the device or by disabling it.
  //  `TrackState.Muted` state is when something outside of the
  //  app muted the stream so it doesn't count.
  active: !device.isSkipped && device.trackState !== TrackState.Disabled,
});

const DeviceManagerPersistenceInjectDependencies: FunctionComponent<IProps> = (props) =>
  <SettingsStorage>{({ value: settings, setValue: saveSettings }) =>
    <StateStorage>{({ value: state, setValue: saveState }) =>
      <DeviceManagerPersistence
        settings={settings}
        state={state}
        saveSettings={saveSettings}
        saveState={saveState}
        {...props}
      />
    }</StateStorage>
  }</SettingsStorage>;

export default DeviceManagerPersistenceInjectDependencies;
